import React, { useCallback, useRef, useState, useEffect } from "react";
import { PhotoLocation } from "../types/protocols";
import { useFirebaseImage, ImageSize, getImagePathWithSize } from "../Helpers/ImageLoader";
import COLORS from "../Helpers/utils";

interface PhotoThumbnailWithMarkerProps {
    photoLocation: PhotoLocation;
    size?: number;
}

const PhotoThumbnailWithMarker: React.FC<PhotoThumbnailWithMarkerProps> = ({
    photoLocation,
    size = 112
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
    
    // Use scaled small resolution image for thumbnail (preserves aspect ratio)
    // Fall back to full-size if scaled version doesn't exist
    const scaledPath = getImagePathWithSize(photoLocation?.storagePath, ImageSize.SCALED_SMALL);
    const fullPath = photoLocation?.storagePath;
    const { src: scaledSrc, hasError: scaledHasError } = useFirebaseImage(scaledPath);
    const { src: fullSrc, isLoading, hasError: fullHasError } = useFirebaseImage(fullPath);
    
    // Use scaled version if available, otherwise fall back to full-size
    const src = scaledHasError ? fullSrc : scaledSrc;
    const hasError = scaledHasError && fullHasError;

    // Calculate container dimensions based on image aspect ratio
    const containerStyle = React.useMemo(() => {
        if (!photoLocation) return { width: `${size}px`, height: `${size}px` };
        
        const { width: photoWidth, height: photoHeight } = photoLocation;
        const aspectRatio = photoWidth / photoHeight;
        
        let width: number;
        let height: number;
        
        if (aspectRatio > 1) {
            // Landscape: max out width
            width = size;
            height = size / aspectRatio;
        } else {
            // Portrait or square: max out height
            height = size;
            width = size * aspectRatio;
        }
        
        return { width: `${width}px`, height: `${height}px` };
    }, [photoLocation, size]);

    const calculateMarkerPosition = useCallback(() => {
        if (!photoLocation || !containerDimensions.width) return null;

        const { x, y, width: photoWidth } = photoLocation;
        const { width: containerWidth } = containerDimensions;

        // Simple scale calculation since container matches image aspect ratio (no cropping)
        const scale = containerWidth / photoWidth;

        // Apply scale to marker position
        const scaledX = x * scale;
        const scaledY = y * scale;

        const markerSize = 20; // Smaller marker for thumbnail
        return {
            left: `${scaledX}px`,
            top: `${scaledY}px`,
            width: `${markerSize}px`,
            height: `${markerSize}px`,
        };
    }, [photoLocation, containerDimensions]);

    // Update container dimensions
    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current) {
                setContainerDimensions({
                    width: containerRef.current.offsetWidth,
                    height: containerRef.current.offsetHeight
                });
            }
        };

        const resizeObserver = new ResizeObserver(updateDimensions);

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        updateDimensions();

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    if (isLoading) {
        return (
            <div
                style={{
                    ...containerStyle,
                    backgroundColor: '#f0f0f0',
                    borderRadius: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    color: '#666'
                }}
            >
                Laddar...
            </div>
        );
    }

    if (hasError || !src) {
        return (
            <div
                style={{
                    ...containerStyle,
                    backgroundColor: '#f0f0f0',
                    borderRadius: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    color: '#666'
                }}
            >
                Foto saknas
            </div>
        );
    }

    const markerPosition = calculateMarkerPosition();

    return (
        <div
            ref={containerRef}
            style={{
                position: 'relative',
                ...containerStyle,
                overflow: 'hidden',
                borderRadius: '5px',
                border: '1px solid #ddd'
            }}
        >
            <img
                src={src}
                alt="Position i foto"
                onLoad={() => {
                    if (containerRef.current) {
                        setContainerDimensions({
                            width: containerRef.current.offsetWidth,
                            height: containerRef.current.offsetHeight
                        });
                    }
                }}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover', // Fills container without cropping since aspect ratios match
                    display: 'block'
                }}
            />

            {/* Render marker */}
            {markerPosition && (
                <div
                    style={{
                        ...markerPosition,
                        backgroundColor: COLORS.primary,
                        position: 'absolute',
                        padding: '4px',
                        borderRadius: '50%',
                        border: '2px solid white',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                        pointerEvents: 'none'
                    }}
                >
                    <div
                        style={{
                            width: '6px',
                            height: '6px',
                            backgroundColor: 'white',
                            borderRadius: '50%'
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default PhotoThumbnailWithMarker;

